import { s } from '@/theme/tokens';
import { Box, Text } from '@/ui';
import { SingleSelectFilterField } from '@/ui/composites/filters/SingleSelectFilterField';
import { NumberSpinner } from '@/ui/primitives/NumberField';
import { TextField } from '@/ui/primitives/TextField';
import { isSingularValue, toPluralUnitLabel, toSingularUnitLabel } from './utils';
import type { ReactElement } from 'react';

const thresholdFieldHeight = 32;
const thresholdValueColumnWidth = 82;
const thresholdUnitColumnWidth = 114;
const thresholdFieldSurfaceBackground = 'var(--surface-page)';

const compactNumberSpinnerSx = {
  width: '100%',
  '& > .MuiBox-root:last-child > .MuiBox-root': {
    gridTemplateColumns: '20px 1fr 20px',
  },
  '& button': {
    width: 20,
    minWidth: 20,
    px: 0,
    lineHeight: 1,
    '& .MuiSvgIcon-root': {
      fontSize: 14,
      display: 'block',
      margin: '0 auto',
    },
  },
  '& input': {
    px: s('xs'),
  },
  '& input:disabled': {
    WebkitTextFillColor: 'text.disabled',
    color: 'text.disabled',
  },
  '& button:disabled': {
    opacity: 1,
    color: 'text.disabled',
  },
  '& button:disabled .MuiSvgIcon-root': {
    color: 'text.disabled',
  },
} as const;

type ThresholdValueUnitRowProps<TUnit extends string> = {
  label: string;
  value: string;
  unit: TUnit;
  templateColumns?: string;
  options: Array<{ value: TUnit; label: string }>;
  error: boolean;
  betweenText?: string;
  contextAtEnd?: boolean;
  pluralizeUnitLabelsByValue?: boolean;
  disabled?: boolean;
  useNumberField?: boolean;
  onValueChange: (next: string) => void;
  onUnitChange: (next: TUnit) => void;
};

export const ThresholdValueUnitRow = <TUnit extends string>({
  label,
  value,
  unit,
  templateColumns,
  options,
  error,
  betweenText,
  contextAtEnd = false,
  pluralizeUnitLabelsByValue = false,
  disabled = false,
  useNumberField = false,
  onValueChange,
  onUnitChange,
}: ThresholdValueUnitRowProps<TUnit>): ReactElement => {
  const selectElement = (
    <SingleSelectFilterField
      fullWidth
      disabled={disabled}
      inputAriaLabel={label}
      inputHeight={thresholdFieldHeight}
      inputBackgroundColor={thresholdFieldSurfaceBackground}
      value={unit}
      options={options.map((option) => ({
        value: option.value,
        label:
          pluralizeUnitLabelsByValue && !isSingularValue(value)
            ? toPluralUnitLabel(option.label)
            : toSingularUnitLabel(option.label),
      }))}
      onChange={(nextValue) => {
        onUnitChange(nextValue as TUnit);
      }}
    />
  );

  const contextElement = betweenText ? (
    <Text
      role="body"
      color="textSecondary"
      sx={{
        whiteSpace: 'nowrap',
        textAlign: 'center',
        justifySelf: 'stretch',
        fontSize: (theme) => theme.typography.subtitle1.fontSize,
        lineHeight: (theme) => theme.typography.subtitle1.lineHeight,
      }}
    >
      {betweenText}
    </Text>
  ) : null;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns:
          templateColumns ??
          (contextAtEnd
            ? `max-content ${thresholdValueColumnWidth}px ${thresholdUnitColumnWidth}px max-content`
            : `max-content ${thresholdValueColumnWidth}px max-content ${thresholdUnitColumnWidth}px`),
        alignItems: 'center',
        columnGap: s('lg'),
        width: '100%',
        maxWidth: '100%',
        '& > *': { minWidth: 0 },
      }}
    >
      <Text
        role="body"
        color="textSecondary"
        sx={{
          display: 'block',
          width: '100%',
          whiteSpace: 'nowrap',
          textAlign: 'center',
          justifySelf: templateColumns ? 'stretch' : 'start',
          fontSize: (theme) => theme.typography.subtitle1.fontSize,
          lineHeight: (theme) => theme.typography.subtitle1.lineHeight,
        }}
      >
        {label}
      </Text>
      <Box
        sx={{
          width: templateColumns
            ? `${thresholdValueColumnWidth}px`
            : `${thresholdValueColumnWidth}px`,
          maxWidth: '100%',
          justifySelf: templateColumns ? 'center' : 'start',
        }}
      >
        {useNumberField ? (
          <NumberSpinner
            value={value}
            min={1}
            step={1}
            size="small"
            inputHeight={thresholdFieldHeight}
            error={error}
            disabled={disabled}
            fieldStyle="settings"
            placeholder="Enter value"
            sx={compactNumberSpinnerSx}
            onChange={onValueChange}
          />
        ) : (
          <TextField
            label={undefined}
            placeholder="Enter value"
            type="number"
            inputHeight={thresholdFieldHeight}
            fieldStyle="settings"
            value={value}
            error={error}
            disabled={disabled}
            sx={[
              { width: '100%' },
              disabled
                ? {
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: 'text.disabled',
                      color: 'text.disabled',
                    },
                  }
                : {},
            ]}
            onChange={(event) => {
              onValueChange(event.target.value);
            }}
          />
        )}
      </Box>
      {contextAtEnd ? (
        <>
          <Box
            sx={{
              width: templateColumns
                ? `${thresholdUnitColumnWidth}px`
                : `${thresholdUnitColumnWidth}px`,
              maxWidth: '100%',
              justifySelf: templateColumns ? 'center' : 'start',
            }}
          >
            {selectElement}
          </Box>
          {contextElement}
        </>
      ) : (
        <>
          {contextElement}
          <Box
            sx={{
              width: templateColumns
                ? `${thresholdUnitColumnWidth}px`
                : `${thresholdUnitColumnWidth}px`,
              maxWidth: '100%',
              justifySelf: templateColumns ? 'center' : 'start',
            }}
          >
            {selectElement}
          </Box>
        </>
      )}
    </Box>
  );
};
